import { useEffect, useState } from "react";
import { adminUsageDraftFromSnapshot, isAdminUsageDraftDirty, parseAdminUsageDraft, type AdminUsageDraft } from "./admin-usage-form";
import { settingsApi } from "./api";
import { Skeleton, SkeletonGrid, SkeletonLines } from "./Skeleton";
import type { AdminUsageSnapshot, UsageWindow } from "./types";
import "./admin-usage.css";

const WINDOWS = [
  { key: "today", label: "今日（上海自然日）" },
  { key: "d7", label: "近 7 日" },
  { key: "d30", label: "近 30 日" },
] as const;
const ENGINES = ["s2.1-pro", "s2.1-pro-free", "s2-pro", "s1"] as const;

function dollars(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }).format(value);
}

function costValue(value: number | null): string {
  return value === null ? "未记录" : dollars(value);
}

function minutes(ms: number): string {
  return `${(ms / 60_000).toFixed(2)} 分钟`;
}

function CostWindows({ value }: { value: UsageWindow }) {
  return <div className="admin-usage-cost-windows">
    {WINDOWS.map(({ key, label }) => <article className="admin-usage-cost-window" key={key}>
      <h4>{label}</h4>
      <dl>
        <div><dt>实际</dt><dd>{costValue(value[key].actual_usd)}</dd></div>
        <div><dt>估算</dt><dd>{costValue(value[key].estimated_usd)}</dd></div>
      </dl>
    </article>)}
  </div>;
}

function UsageBlock({ title, description, costs, children }: {
  title: string;
  description: string;
  costs: UsageWindow;
  children?: React.ReactNode;
}) {
  return <section className="admin-usage-block">
    <div className="admin-usage-block-heading">
      <div><h3>{title}</h3><p>{description}</p></div>
    </div>
    <CostWindows value={costs} />
    {children}
  </section>;
}

export function AdminUsageSettings() {
  const [usage, setUsage] = useState<AdminUsageSnapshot | null>(null);
  const [draft, setDraft] = useState<AdminUsageDraft | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState<"save" | "reset" | null>(null);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");

  async function reload() {
    setLoading(true);
    setError("");
    setStatus("");
    try {
      const next = await settingsApi.loadAdminUsage();
      setUsage(next);
      setDraft(adminUsageDraftFromSnapshot(next));
    } catch {
      setUsage(null);
      setDraft(null);
      setError("无法读取用量统计。");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void reload(); }, []);

  function changeDraft(patch: Partial<AdminUsageDraft>) {
    setDraft((current) => current ? { ...current, ...patch } : current);
    setError("");
    setStatus("");
  }

  async function save() {
    if (!usage || !draft) return;
    const parsed = parseAdminUsageDraft(draft, usage.image_daily.max_limit);
    if (!parsed.value) {
      setError(parsed.error ?? "用量设置无效。");
      return;
    }

    setBusyAction("save");
    setError("");
    setStatus("");
    try {
      const next = await settingsApi.updateAdminUsageSettings({
        ...parsed.value,
        expected_tts_revision: usage.tts_daily.revision,
        request_id: crypto.randomUUID(),
      });
      // PUT 的响应是新的服务器权威快照；成功后才同时推进基线和输入。
      setUsage(next);
      setDraft(adminUsageDraftFromSnapshot(next));
      setStatus("用量设置已保存。");
    } catch {
      // draft 保持原值，让管理员可直接修正或重试。
      setError("用量设置保存失败，草稿已保留，请重试。");
    } finally {
      setBusyAction(null);
    }
  }

  async function resetImageUsage() {
    setBusyAction("reset");
    setError("");
    setStatus("");
    try {
      await settingsApi.resetAdminImageUsage(crypto.randomUUID());
      const next = await settingsApi.loadAdminUsage();
      // 重置是独立动作：刷新服务器统计与配置基线，但绝不覆盖当前配置草稿。
      setUsage(next);
      setStatus("今日 CivitAI 图片用量已重置；未保存的配置草稿保持不变。");
    } catch {
      setError("今日 CivitAI 图片用量重置失败，请稍后重试；配置草稿保持不变。");
    } finally {
      setBusyAction(null);
    }
  }

  if (loading) {
    return <div className="admin-usage-panel" role="status" aria-label="正在读取用量统计">
      <div className="admin-usage-toolbar"><Skeleton variant="control" /></div>
      {[0, 1, 2].map((item) => <section className="admin-usage-block" key={item}><SkeletonLines /><SkeletonGrid items={3} variant="row" /></section>)}
    </div>;
  }
  if (!usage || !draft) {
    return <div className="panel-error" role="alert"><p>{error || "无法读取用量统计。"}</p><button type="button" onClick={() => void reload()}>重试</button></div>;
  }

  const busy = busyAction !== null;
  const dirty = isAdminUsageDraftDirty(usage, draft);
  const validation = parseAdminUsageDraft(draft, usage.image_daily.max_limit);
  const validationError = dirty ? validation.error ?? "" : "";
  const displayedError = error || validationError;
  const confirmedPending = usage.tts_daily.pending_duration.filter((item) => item.billing_status === "confirmed").length;
  const uncertainPending = usage.tts_daily.pending_duration.filter((item) => item.billing_status === "uncertain").length;

  return <div className="admin-usage-panel">
    <div className="admin-usage-toolbar">
      <span className={displayedError ? "action-error" : "admin-usage-status"} role={displayedError ? "alert" : "status"} aria-live="polite">{displayedError || status}</span>
      <button className="primary admin-usage-save" type="button" disabled={!dirty || !validation.value || busy} onClick={() => void save()}>{busyAction === "save" ? "保存中…" : "保存用量设置"}</button>
    </div>

    <p className="admin-usage-caption">币种 USD；费用窗口按 Asia/Shanghai 自然日。实际供应商费用与调用时价格估算分开，缺少回执显示“未记录”。</p>

    <UsageBlock title="LLM" description="语言模型调用费用" costs={usage.costs.llm} />

    <UsageBlock title="TTS" description="语音生成费用与每日配额" costs={usage.costs.tts}>
      <div className="admin-usage-fields admin-usage-fields-tts">
        <label>模型选择<select disabled={busy} value={draft.engine} onChange={(event) => changeDraft({ engine: event.target.value as AdminUsageDraft["engine"] })}>{ENGINES.map((value) => <option key={value}>{value}</option>)}</select></label>
        <label>用户配额（分钟）<input disabled={busy} inputMode="decimal" placeholder="留空不限" value={draft.audioUser} onChange={(event) => changeDraft({ audioUser: event.target.value })} /></label>
        <label>全局配额（分钟）<input disabled={busy} inputMode="decimal" placeholder="留空不限" value={draft.audioGlobal} onChange={(event) => changeDraft({ audioGlobal: event.target.value })} /></label>
      </div>
      <div className="admin-usage-facts">
        <p>今日已结算 <strong>{minutes(usage.tts_daily.used_ms)}</strong>；按真实音频时长计算，额度留空表示不限。</p>
        {usage.tts_daily.pending_duration.length > 0 ? <p className="admin-usage-warning">有 {usage.tts_daily.pending_duration.length} 条成本已记录但真实时长待处理（确认收费 {confirmedPending}，计费不确定 {uncertainPending}）。这些记录不会伪装为 0 分钟，也不会自动释放额度。</p> : null}
      </div>
    </UsageBlock>

    <UsageBlock title="CivitAI" description="图片生成费用与每日配额" costs={usage.costs.image}>
      <div className="admin-usage-fields admin-usage-fields-civitai">
        <label>用户配额（张）<input disabled={busy} type="number" min="0" max={usage.image_daily.max_limit} placeholder="留空不限" value={draft.imageUser} onChange={(event) => changeDraft({ imageUser: event.target.value })} /></label>
        <label>全局配额（张）<input disabled={busy} required type="number" min="0" max={usage.image_daily.max_limit} value={draft.imageGlobal} onChange={(event) => changeDraft({ imageGlobal: event.target.value })} /></label>
      </div>
      <div className="admin-usage-facts admin-usage-image-facts">
        <p>全局已用 <strong>{usage.image_daily.used} 张</strong>；个人用量 {usage.image_daily.users.length ? usage.image_daily.users.map((item) => `${item.user_id}: ${item.used}`).join("，") : "暂无"}。图片沿用现有 UTC 日界。</p>
        <button className="danger" type="button" disabled={busy} onClick={() => void resetImageUsage()}>{busyAction === "reset" ? "重置中…" : "重置今日图片用量"}</button>
      </div>
    </UsageBlock>
  </div>;
}
